import React from 'react'
import { cssTransition, toast, ToastOptions } from 'react-toastify'
import { undo, skipUndo } from '../state/requests'
import { useDispatch } from 'react-redux'
import 'react-toastify/dist/ReactToastify.css'
import { useNewTranslation } from 'hooks'
import { StyledButton } from 'particles/Alert/style'
import ConfirmationAlert, { AlertContentType, AlertButtons } from 'particles/Alert/index'
import Snackbar from 'particles/Alert/Snackbar'

interface CloseButtonsProps {
    toastType: AlertContentType
    closeToast?: () => void
    undoAction?: {
        countdown: number
        undoId: string
    }
    dataTestId?: string
}

const CloseButtons: React.FC<CloseButtonsProps> = ({ closeToast, undoAction, toastType, dataTestId }) => {
    const dispatch = useDispatch()
    const [t] = useNewTranslation()
    const dispatchUndo = (undoId: string) => () => dispatch(undo({ undoId: undoId }))
    const dispatchSkipUndo = (undoId: string) => () => dispatch(skipUndo({ undoId: undoId }))
    return (
        <>
            {undoAction ? (
                <AlertButtons
                    contentType={toastType}
                    primaryButtonText={t('button.accept')}
                    onClickPrimaryButton={dispatchSkipUndo(undoAction.undoId)}
                    secondaryButtonText={t('button.recall')}
                    onClickSecondaryButton={dispatchUndo(undoAction.undoId)}
                />
            ) : (
                <StyledButton data-testid={dataTestId} contentType={toastType} onClick={closeToast}>
                    {t('button.ok').toUpperCase()}
                </StyledButton>
            )}
        </>
    )
}
interface ShowToastOptions {
    title: string
    message?: string
    toastType: AlertContentType
    autoCloseTimer?: number
    undoAction?: {
        countdown: number
        undoId: string
    }
}

const getToastConfig = (options: ShowToastOptions) => {
    const { toastType, undoAction, autoCloseTimer, title, message } = options
    const Content = <ConfirmationAlert contentType={toastType} title={title} subText={message} />

    return {
        ToastContent: Content,
        toastOptions: {
            position: toast.POSITION.BOTTOM_CENTER,
            className: `toast__${toastType} type__toast`,
            progressClassName: `progressBar__${toastType}`,
            containerId: 'toast-container',
            autoClose:
                undoAction && undoAction.hasOwnProperty('countdown')
                    ? undoAction.countdown
                    : autoCloseTimer
                      ? autoCloseTimer
                      : (false as const),
            closeButton: (
                <CloseButtons dataTestId="test-toast-close-button" undoAction={undoAction} toastType={toastType} />
            ),
            pauseOnHover: false,
            pauseOnFocusLoss: false,
        },
    }
}

/**
 * @description only use this function for confirmation alert. Use `showSnackBar` when only showing toast with no confirmation needed
 */
export const showToast = (options: ShowToastOptions): any => {
    const { ToastContent, toastOptions } = getToastConfig(options)
    return toast(ToastContent, toastOptions)
}

export const updateToast = (toastId: any, options: ShowToastOptions) => {
    const { ToastContent, toastOptions } = getToastConfig(options)
    toast.update(toastId, {
        render: ToastContent,
        ...toastOptions,
    })
}

const FadeInTransition = cssTransition({
    enter: 'snack-bar-fade-in',
    exit: 'snack-bar-fade-out',
    collapse: true,
    collapseDuration: 0,
    duration: 200,
})

interface ShowSnackBarOptions {
    title: string
    type: 'positive' | 'error'
}

const getSnackBarConfig = (options: ShowSnackBarOptions) => {
    const { type, title } = options
    const Content = <Snackbar type={type} text={title} />

    const toastOptions: ToastOptions = {
        position: toast.POSITION.BOTTOM_CENTER,
        className: `toast__${type} type__snackbar`,
        progressClassName: `progressBar__${type}`,
        autoClose: 3000,
        containerId: 'snackbar-container',
        closeButton: false,
        pauseOnHover: true,
        pauseOnFocusLoss: true,
        hideProgressBar: true,
        transition: FadeInTransition,
    }

    return {
        ToastContent: Content,
        toastOptions,
    }
}
export const showSnackBar = (options: ShowSnackBarOptions) => {
    toast.dismiss()
    const { ToastContent, toastOptions } = getSnackBarConfig(options)

    return setTimeout(() => toast(ToastContent, toastOptions), 300)
}
